import { useCallback, memo, MouseEvent } from "react";
import { useAppDispatch, useAppSelector } from "stores";
import styled from "styled-components";
import store from "stores/interfaces";
import {
  ListItemText,
  IconButton,
  ListItemAvatar,
  Avatar,
  ListItemIcon,
  Checkbox,
  ListItemButton,
  ListItem,
} from "@mui/material";

import LibraryMusicIcon from "@mui/icons-material/LibraryMusic";
import PublicIcon from "@mui/icons-material/Public";
import EditIcon from "@mui/icons-material/Edit";
import { useTranslation } from "react-i18next";

type MediumListItemProps = {
  mediumId: string;
  onEdit?: (props: { anchorEl: Element; mediumId: string }) => void;
};

const MediumListItem = ({ mediumId, onEdit }: MediumListItemProps) => {
  const [t] = useTranslation();
  const d = useAppDispatch();
  const dir = useAppSelector((state) =>
    store.getAppState(state, "openRoomMediaDir")
  );
  const medium = useAppSelector((state) =>
    store.getMediumById(state, mediumId)
  );
  const selectingMedia = useAppSelector((state) => {
    return store.getAppState(state, "selectingMedia");
  });
  const selected = useAppSelector((state) => {
    return store.getAppState(state, "selectedMediaIds").includes(mediumId);
  });

  const onSelect = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (!onEdit) return;
      e.stopPropagation();
      onEdit({
        anchorEl: e.currentTarget,
        mediumId,
      });
    },
    [mediumId, onEdit]
  );
  const onPlay = useCallback(() => {
    if (medium.url) {
      if (dir === "bgm") {
        d(
          store.updateCurrentRoomField({
            mediaUrl: medium.url,
            mediaRef: null,
            mediaType: "file",
            mediaName: medium.name,
            mediaVolume: medium.volume,
            mediaRepeat: medium.loop,
          })
        );
      } else if (dir === "sound") {
        d(
          store.updateCurrentRoomField({
            soundUrl: medium.url,
            soundRef: null,
            soundName: medium.name,
            soundVolume: medium.volume,
            soundRepeat: medium.loop,
          })
        );
      } else if (dir === "effect") {
        d(
          store.updateCurrentRoomEffect({
            soundUrl: medium.url,
            soundRef: null,
            soundName: medium.name,
          })
        );
      }
    }
  }, [medium, dir, d]);

  const onSelectMedia = useCallback(() => {
    d(
      store.appStateMutate((state) => {
        const index = state.selectedMediaIds.indexOf(mediumId);
        if (index < 0) {
          state.selectedMediaIds.push(mediumId);
        } else {
          state.selectedMediaIds.splice(index, 1);
        }
      })
    );
  }, [d, mediumId]);

  const handleClick = useCallback(() => {
    if (selectingMedia) {
      onSelectMedia();
    } else {
      onPlay();
    }
  }, [selectingMedia, onPlay, onSelectMedia]);

  if (!medium) {
    return null;
  }
  return (
    <ListItem
      component="div"
      secondaryAction={
        <IconButton
          onClick={onSelect}
          edge="end"
          size="large"
          aria-label={t("メディアを編集する")}
        >
          <EditIcon />
        </IconButton>
      }
      disablePadding
    >
      <ListItemButton onClick={handleClick} disabled={!medium.url}>
        {selectingMedia && (
          <ListItemIcon>
            <Checkbox
              checked={selected}
              disableRipple
              color="secondary"
              sx={{
                padding: "8px",
              }}
            />
          </ListItemIcon>
        )}
        <ListItemAvatar>
          <Avatar>
            <Avatar>
              {medium.external ? <PublicIcon /> : <LibraryMusicIcon />}
            </Avatar>
          </Avatar>
        </ListItemAvatar>
        <ListItemTextWrapped
          primary={medium.name}
          // secondary={`volume:${medium.volume} loop:${medium.loop ? "on" : "off"}`}
        />
      </ListItemButton>
    </ListItem>
  );
};

const ListItemTextWrapped = styled(ListItemText)`
  word-wrap: break-word;
`;

export default memo(MediumListItem);
